import { TransitionEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import cn from "classnames";
import { toast } from "react-hot-toast";
import Dropdown from "../../Settings/Dropdown";
import AiDisclaimer from "../AiDisclaimer";
import { isNewSince, newRowClass, UpdatesBanner } from "../updates";
import { PhoneIcon, DocumentTextIcon, FlagIcon, SparklesIcon, CheckIcon } from "@heroicons/react/24/solid";
import { ArrowTopRightOnSquareIcon, EllipsisVerticalIcon, EyeIcon, EyeSlashIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { createRevealSequence } from "../../common/Reveal";
import { useNowPlaying } from "../NowPlayingContext";
import AnalyzingState from "../AnalyzingState";
import TimelineNoteModal, { TimelineNoteTarget } from "../TimelineNoteModal";
import { callKey, fileKey, timelineEntryKey } from "../timelineKey";
import { TimelineOverride } from "../../../db/schema";
import { api, CallListItem, FileListItem, InvestigationDetail, LeadListItem, LEAD_CATEGORY_LABELS, SEVERITY_CLASSES, callParties, formatMs, scoreClass } from "../api";

// The timeline reads as a case chronology: every recorded call is a card
// anchored to its day, the AI brief's narrative for that call sits inside it,
// and the flagged moments hang off the call they came from (linking to the
// exact second). Brief events that have no calendar date — "the night of the
// incident" — open the page instead of being thrown away.
//
// Every item has a hover menu (same kebab as the case transcription view):
// open the source, add a detective's note, or hide it. Overrides are stored
// on the investigation keyed by timelineKey.ts and survive brief
// regeneration; the AI's text is never edited or deleted.

type Flag = LeadListItem;
type BriefEntry = { key: string; text: string; callId?: string; leadId?: string; fileId?: string; page?: number };
type Item =
  | { kind: "call"; key: string; at: Date; call: CallListItem; narrative: BriefEntry[]; flags: Flag[] }
  | { kind: "file"; key: string; at: Date; file: FileListItem; covers?: Date; addedAt: Date; anchored: boolean }
  | { kind: "brief"; key: string; at: Date; text: string; callId?: string; leadId?: string; fileId?: string; page?: number };

type KindFilter = "call" | "flag" | "file";
type Overrides = Record<string, TimelineOverride>;

const MONTH = (d: Date) => d.toLocaleDateString([], { month: "short" });
const WEEKDAY = (d: Date) => d.toLocaleDateString([], { weekday: "short" });
const TIME = (d: Date) => d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

// Brief dates arrive as "2025-08-06" (calendar) or prose ("Undated, night of
// the incident"). Parse the former as local time so it lands on the right day.
function parseBriefDate(s: string): Date | null {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

const FILE_KIND_LABEL: Record<string, string> = { pdf: "Document", photo: "Photo", spreadsheet: "Spreadsheet", other: "File" };

// A chronology anchors files to when their contents happened, not when they
// were uploaded: documents and photos to their earliest key date (a message
// timestamp, a dated receipt), spreadsheets to their data's date span. Files
// with no event dates (undated photos and docs) fall back to the upload date.
function fileEventAnchor(f: FileListItem): { at: Date; end?: Date } | null {
  const md = (f.metadata as any) || {};
  if ((f.kind === "document" || f.kind === "photo" || f.kind === "video") && Array.isArray(md.keyDates)) {
    const ds = (md.keyDates as { date: string }[])
      .map((k) => parseBriefDate(String(k.date || "")))
      .filter((d): d is Date => Boolean(d))
      .sort((a, b) => a.getTime() - b.getTime());
    if (ds.length) return { at: ds[0], end: ds.length > 1 ? ds[ds.length - 1] : undefined };
  }
  if (f.kind === "spreadsheet" && md.dateSpan?.start) {
    const start = parseBriefDate(String(md.dateSpan.start).slice(0, 10));
    const end = md.dateSpan.end ? parseBriefDate(String(md.dateSpan.end).slice(0, 10)) : null;
    if (start) return { at: start, end: end && end.getTime() !== start.getTime() ? end : undefined };
  }
  return null;
}

const SPAN_DATE = (d: Date) => d.toLocaleDateString([], { month: "short", day: "numeric" });

// Height collapse for hiding / revealing timeline items, so neighbours slide
// up and down instead of jumping. Same CSS-grid 0fr -> 1fr technique as
// Settings/ConditionalFieldGroup, without that component's field styling.
// Overflow is released once fully open so a card's dropdown menu isn't
// clipped at the row's edge.
const Collapse: React.FC<{ open: boolean; children: React.ReactNode; className?: string }> = ({ open, children, className }) => {
  const [fullyOpen, setFullyOpen] = useState(open);
  useEffect(() => {
    if (!open) setFullyOpen(false);
  }, [open]);
  const onEnd = (e: TransitionEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && open) setFullyOpen(true);
  };
  return (
    <div className={cn("grid transition-all duration-300 ease-in-out", open ? "grid-rows-[1fr] opacity-100 visible" : "grid-rows-[0fr] opacity-0 invisible")} aria-hidden={!open} onTransitionEnd={onEnd}>
      <div className={cn(open && fullyOpen ? "overflow-visible" : "overflow-hidden")}>
        <div className={className}>{children}</div>
      </div>
    </div>
  );
};

// Hover kebab, mirrored from the case transcription segment menu. Cards that
// host it carry `focus-within:z-30` so the open menu paints above the next
// card, and hidden items dim their *content* rather than the card wrapper so
// the menu itself never inherits the opacity. The
// `dropdown` wrapper fades in on the parent `group`'s hover, DaisyUI opens
// the menu on focus, and every item blurs to close. Clicks stop here so the
// card underneath doesn't navigate.
const ItemMenu: React.FC<{ onOpen: (() => void) | null; hidden: boolean; hasNote: boolean; onNote: () => void; onToggleHide: () => void; compact?: boolean; inline?: boolean; className?: string }> = ({
  onOpen,
  hidden,
  hasNote,
  onNote,
  onToggleHide,
  compact,
  inline,
  className,
}) => {
  const close = () => (document.activeElement as HTMLElement | null)?.blur();
  const item = "text-left hover:bg-blue-50 dark:hover:bg-blue-900/40 p-2 flex items-center gap-2 text-sm";
  return (
    <div
      className={cn(
        // dropdown-bottom is explicit: DaisyUI only sets `top: 100%` when a
        // direction class is present, and without it the menu's static
        // position inside an inline-flex wrapper lands on top of the trigger.
        "dropdown dropdown-end dropdown-bottom z-20",
        // Inline: lives in the flag/score row. At rest it has no width, so the
        // cluster sits flush right; on card hover it grows to its width (the
        // cluster slides left to make room) and fades in. No overflow clip,
        // since the menu is positioned inside this wrapper. Floating: fades
        // in at the card corner.
        inline
          ? "inline-flex justify-end w-0 group-hover:w-6 focus-within:w-6 transition-[width] duration-150 ease-out"
          : "absolute opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150",
        !inline && (compact ? "right-0 top-0" : "right-2 top-2"),
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        tabIndex={0}
        className={cn(
          "group/actions hover:bg-blue-500 rounded-full",
          inline
            ? "p-0.5 -ml-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150 pointer-events-none group-hover:pointer-events-auto focus-within:pointer-events-auto"
            : "p-0.5 bg-base-100/80",
          compact && "scale-90"
        )}
        title="Actions"
        onClick={(e) => e.stopPropagation()}
      >
        <EllipsisVerticalIcon
          className={cn(
            "transition-colors",
            "size-5 group-hover/actions:text-white",
            inline ? "text-base-content/50" : "text-base-content/60"
          )}
        />
      </button>
      <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-lg z-[1] w-48 p-2 shadow dark:border dark:border-base-300 mt-1">
        {onOpen && (
          <li className="p-0 m-0">
            <button type="button" className={item} onClick={(e) => { e.stopPropagation(); close(); onOpen(); }}>
              <ArrowTopRightOnSquareIcon className="size-4" /> Open source
            </button>
          </li>
        )}
        <li className="p-0 m-0">
          <button type="button" className={item} onClick={(e) => { e.stopPropagation(); close(); onNote(); }}>
            <PencilSquareIcon className="size-4" /> {hasNote ? "Edit note" : "Add note"}
          </button>
        </li>
        <li className="p-0 m-0">
          <button type="button" className={item} onClick={(e) => { e.stopPropagation(); close(); onToggleHide(); }}>
            {hidden ? <EyeIcon className="size-4" /> : <EyeSlashIcon className="size-4" />} {hidden ? "Unhide" : "Hide"}
          </button>
        </li>
      </ul>
    </div>
  );
};

const Note: React.FC<{ o?: TimelineOverride; className?: string }> = ({ o, className }) =>
  o?.note ? (
    <p className={cn("text-xs text-blue-700 dark:text-blue-300 leading-5", className)}>
      <span className="font-semibold">Detective's note:</span> {o.note}
      {o.updatedBy && <span className="text-blue-700/60 dark:text-blue-300/60"> · {o.updatedBy}</span>}
    </p>
  ) : null;

const HiddenChip: React.FC = () => (
  <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 rounded-full bg-base-300 text-base-content/60">
    <EyeSlashIcon className="size-3" /> Hidden
  </span>
);

const TimelineTab: React.FC<{ investigationId: string; detail: InvestigationDetail; reanalyzing?: boolean; updates?: number; since?: string; onSeen?: () => void; clearing?: boolean; onRefresh?: () => void }> = ({
  investigationId,
  detail,
  reanalyzing,
  updates = 0,
  since,
  onSeen,
  clearing,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const { play, nowPlaying } = useNowPlaying();
  const [calls, setCalls] = useState<CallListItem[]>([]);
  const [files, setFiles] = useState<FileListItem[]>([]);
  const [leads, setLeads] = useState<LeadListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState<Record<KindFilter, boolean>>({ call: true, flag: true, file: true });
  const [showHidden, setShowHidden] = useState(false);
  const [minSeverity, setMinSeverity] = useState(3);
  const [overrides, setOverrides] = useState<Overrides>((detail.timeline_overrides as Overrides | null) || {});
  const [noteTarget, setNoteTarget] = useState<TimelineNoteTarget | null>(null);

  useEffect(() => {
    setOverrides((detail.timeline_overrides as Overrides | null) || {});
  }, [detail.timeline_overrides]);

  useEffect(() => {
    Promise.all([api.calls(investigationId).then(setCalls), api.files(investigationId).then(setFiles), api.leads(investigationId).then(setLeads)])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [investigationId]);

  const setOverride = useCallback(
    async (key: string, body: { hidden?: boolean; note?: string }) => {
      try {
        const { overrides: next } = await api.setTimelineOverride(investigationId, key, body);
        setOverrides(next);
        onRefresh?.();
      } catch (e: any) {
        toast.error(e?.response?.data?.error || "Could not save the change");
        throw e;
      }
    },
    [investigationId, onRefresh]
  );
  const isHidden = (key: string) => Boolean(overrides[key]?.hidden);
  const toggleHide = (key: string) => setOverride(key, { hidden: !isHidden(key) }).catch(() => {});
  const openNote = (key: string, label: string, preview?: React.ReactNode) => setNoteTarget({ key, label, note: overrides[key]?.note || "", preview });

  // Previews for the note modal: the item as it looks on the timeline,
  // minus hover chrome, so the whole entry is readable while annotating.
  const briefPreview = (text: string) => (
    <div className="rounded-lg border border-purple-200 dark:border-purple-900 bg-purple-50/60 dark:bg-purple-950/20 p-3 flex items-start gap-3">
      <SparklesIcon className="size-4 text-purple-500 shrink-0 mt-0.5" />
      <p className="text-sm text-base-content/80 leading-5">{text}</p>
    </div>
  );
  const undatedPreview = (label: string, text: string) => (
    <div className="rounded-lg border border-base-300 bg-base-100 p-3">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-400">{label || "Undated"}</p>
      <p className="text-sm text-base-content mt-0.5 leading-5">{text}</p>
    </div>
  );
  const callPreview = (c: CallListItem, at: Date, narrative: BriefEntry[], flagCount: number, highlightKey?: string) => (
    <div className="rounded-lg border border-base-300 bg-base-100 p-4">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-blue-50 dark:bg-blue-900/40 rounded-lg shrink-0">
          <PhoneIcon className="h-5 w-5 text-blue-500 dark:text-blue-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xs tabular-nums font-semibold text-base-content/70">{TIME(at)}</span>
            <span className="text-sm font-medium text-base-content">{callParties(c).primary}</span>
            {c.duration_ms ? <span className="text-xs text-base-content/40 tabular-nums">{formatMs(c.duration_ms)}</span> : null}
            <span className="ml-auto flex items-center gap-1.5">
              {flagCount > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-base-content/50">
                  <FlagIcon className="size-3 text-amber-500" /> {flagCount}
                </span>
              )}
              {c.relevance_score != null && <span className={cn("text-[11px] font-bold px-2 py-0.5 rounded-full tabular-nums", scoreClass(c.relevance_score))}>{c.relevance_score}</span>}
            </span>
          </div>
          {narrative.length > 0 ? (
            narrative.map((n) => (
              <p key={n.key} className={cn("text-sm leading-5 mt-1.5", highlightKey ? (n.key === highlightKey ? "text-base-content" : "text-base-content/40") : "text-base-content/80")}>
                {n.text}
              </p>
            ))
          ) : c.relevance_reason || c.summary ? (
            <p className="text-sm text-base-content/70 leading-5 mt-1.5">{c.relevance_reason || c.summary}</p>
          ) : (
            <p className="text-xs text-base-content/40 mt-1.5">{c.file.original_file_name}</p>
          )}
        </div>
      </div>
    </div>
  );
  const filePreview = (f: FileListItem, headline: React.ReactNode) => {
    const desc = (f.metadata as any)?.description as string | undefined;
    return (
      <div className="rounded-lg border border-base-300 bg-base-100 p-3 flex items-start gap-3">
        <div className="p-2 bg-base-200 rounded-lg shrink-0">
          <DocumentTextIcon className="h-5 w-5 text-base-content/50" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">{headline}</div>
          <p className="text-sm font-medium text-base-content">{f.original_file_name}</p>
          {desc && <p className="text-xs text-base-content/60 mt-0.5">{desc}</p>}
        </div>
      </div>
    );
  };

  const { days, undated, stats, hiddenCount, shownCount } = useMemo(() => {
    const briefByCall = new Map<string, BriefEntry[]>();
    const briefDated: (BriefEntry & { at: Date })[] = [];
    const undated: (BriefEntry & { label: string })[] = [];
    for (const t of detail.brief?.timeline || []) {
      const d = parseBriefDate(t.date);
      const entry: BriefEntry = { key: timelineEntryKey(t), text: t.description, callId: t.callId, leadId: t.leadId, fileId: t.fileId, page: t.page };
      if (d && t.callId) briefByCall.set(t.callId, [...(briefByCall.get(t.callId) || []), entry]);
      else if (d) briefDated.push({ ...entry, at: d });
      else undated.push({ ...entry, label: t.date.replace(/^undated[,:]?\s*/i, "") });
    }

    const flagsByCall = new Map<string, Flag[]>();
    for (const l of leads) {
      if (!l.call_id || l.status === "dismissed" || l.severity < minSeverity) continue;
      flagsByCall.set(l.call_id, [...(flagsByCall.get(l.call_id) || []), l]);
    }

    const items: Item[] = [];
    for (const c of calls) {
      if (!c.call_started_at) continue;
      const flags = (flagsByCall.get(c.id) || []).sort((a, b) => (a.start_ms || 0) - (b.start_ms || 0));
      items.push({ kind: "call", key: callKey(c.id), at: new Date(c.call_started_at), call: c, narrative: briefByCall.get(c.id) || [], flags });
    }
    for (const f of files) {
      if (f.kind === "jail_call" || !f.created_at) continue;
      const anchor = fileEventAnchor(f);
      items.push({
        kind: "file",
        key: fileKey(f.id),
        at: anchor?.at || new Date(f.created_at),
        file: f,
        covers: anchor?.end,
        addedAt: new Date(f.created_at),
        anchored: Boolean(anchor),
      });
    }
    for (const b of briefDated) items.push({ kind: "brief", key: b.key, at: b.at, text: b.text, callId: b.callId, leadId: b.leadId, fileId: b.fileId, page: b.page });

    // Hidden items (and hidden narrative lines inside calls) count once so
    // the toggle can say how many there are.
    const hiddenKeys = new Set<string>();
    for (const it of items) {
      if (overrides[it.key]?.hidden) hiddenKeys.add(it.key);
      if (it.kind === "call") for (const n of it.narrative) if (overrides[n.key]?.hidden) hiddenKeys.add(n.key);
    }
    for (const u of undated) if (overrides[u.key]?.hidden) hiddenKeys.add(u.key);

    // Hidden items stay in the tree (collapsed) so hiding and revealing
    // animate; only the kind filters remove items outright.
    const listed = items.filter((i) => (i.kind === "call" ? show.call : i.kind === "file" ? show.file : show.call));
    listed.sort((a, b) => a.at.getTime() - b.at.getTime());
    const isShown = (key: string) => showHidden || !overrides[key]?.hidden;

    const days: { key: string; date: Date; items: Item[]; shown: number }[] = [];
    for (const it of listed) {
      const k = dayKey(it.at);
      const last = days[days.length - 1];
      const shown = isShown(it.key) ? 1 : 0;
      if (last && last.key === k) {
        last.items.push(it);
        last.shown += shown;
      } else days.push({ key: k, date: it.at, items: [it], shown });
    }
    const shownItems = listed.filter((i) => isShown(i.key));
    const flagCount = items.reduce((n, i) => n + (i.kind === "call" ? i.flags.length : 0), 0);
    const first = shownItems[0]?.at;
    const lastAt = shownItems[shownItems.length - 1]?.at;
    return {
      days,
      undated,
      stats: { calls: items.filter((i) => i.kind === "call").length, flags: flagCount, days: days.filter((d) => d.shown > 0).length, first, last: lastAt },
      hiddenCount: hiddenKeys.size,
      shownCount: shownItems.length + undated.filter((u) => isShown(u.key)).length,
    };
  }, [calls, files, leads, detail.brief, show, minSeverity, overrides, showHidden]);

  const undatedCalls = calls.filter((c) => !c.call_started_at).length;

  if (reanalyzing) {
    return <AnalyzingState title="Re-analyzing every call" body="Flagged moments are being re-read from each call. The timeline fills back in as calls finish." />;
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 max-w-4xl">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-6">
            <div className="w-16 h-14 rounded-lg bg-base-300" />
            <div className="flex-1 h-24 rounded-lg bg-base-300" />
          </div>
        ))}
      </div>
    );
  }

  const itemShown = (key: string) => showHidden || !overrides[key]?.hidden;

  if (shownCount === 0 && hiddenCount === 0) {
    return (
      <div className="rounded-lg border border-dashed border-base-300 bg-base-100 p-12 text-center max-w-4xl">
        <p className="font-medium text-base-content">Nothing dated yet</p>
        <p className="text-sm text-base-content/60 mt-1">Call dates come from the audio file's tags or filename; you can also set them on each call.</p>
      </div>
    );
  }

  const enter = createRevealSequence();
  let lastMonth = "";

  return (
    <div className={cn("max-w-4xl", nowPlaying && "pb-40")}>
      {onSeen && <UpdatesBanner tab="timeline" count={updates} since={since} onDismiss={onSeen} clearing={clearing} className="mb-4" />}
      {/* Summary strip + filters */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
        <p className="text-sm text-base-content/70">
          <span className="font-semibold text-base-content">{stats.calls}</span> call{stats.calls === 1 ? "" : "s"} over{" "}
          <span className="font-semibold text-base-content">{stats.days}</span> day{stats.days === 1 ? "" : "s"}
          {stats.first && stats.last && (
            <span className="text-base-content/50">
              {" "}· {stats.first.toLocaleDateString([], { month: "short", day: "numeric" })} – {stats.last.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
            </span>
          )}
          {stats.flags > 0 && (
            <>
              {" "}· <span className="font-semibold text-base-content">{stats.flags}</span> flagged moment{stats.flags === 1 ? "" : "s"}
            </>
          )}
        </p>
        <div className="flex items-center gap-1.5 flex-wrap">
          {(
            [
              ["call", "Calls", PhoneIcon],
              ["flag", "Flags", FlagIcon],
              ["file", "Documents", DocumentTextIcon],
            ] as [KindFilter, string, typeof PhoneIcon][]
          ).map(([k, label, Icon]) => (
            <button
              key={k}
              onClick={() => setShow((s) => ({ ...s, [k]: !s[k] }))}
              className={cn(
                "inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors",
                show[k] ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 border-blue-200 dark:border-blue-800" : "border-base-300 text-base-content/50 hover:bg-base-200"
              )}
            >
              <Icon className="size-3" /> {label}
            </button>
          ))}
          {hiddenCount > 0 && (
            <button
              onClick={() => setShowHidden((v) => !v)}
              className={cn(
                "inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors",
                showHidden ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 border-blue-200 dark:border-blue-800" : "border-base-300 text-base-content/50 hover:bg-base-200"
              )}
              title={showHidden ? "Hide the items you hid" : "Show the items you hid"}
            >
              <EyeSlashIcon className="size-3" /> Hidden ({hiddenCount})
            </button>
          )}
          {show.flag && (
            <Dropdown size="xs" className="ml-1" contentClassName="w-28" options={[1, 2, 3, 4, 5].map((s) => ({ value: String(s), label: `sev ≥ ${s}` }))} value={String(minSeverity)} onChange={(v) => setMinSeverity(Number(v))} />
          )}
        </div>
      </div>

      <AiDisclaimer kind="timeline" className="mb-6" />

      {/* Undated narrative from the brief */}
      {undated.length > 0 && (
        <section {...enter()}>
        <Collapse open={undated.some((u) => itemShown(u.key))} className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <SparklesIcon className="size-4 text-purple-500" />
            <h2 className="text-sm font-semibold text-gray-600 dark:text-base-content/70 uppercase tracking-wider">Before the recordings</h2>
            <span className="text-xs text-base-content/40">from the inmate's own account</span>
          </div>
          <ol className="ml-[4.5rem] border-l-2 border-dashed border-purple-300 dark:border-purple-900 pl-5">
            {undated.map((u) => {
              const o = overrides[u.key];
              const open = u.callId ? () => navigate(`/investigations/${investigationId}/calls/${u.callId}`) : null;
              return (
                <li key={u.key}>
                <Collapse open={itemShown(u.key)} className="relative pb-3">
                  <span className="absolute -left-[1.6rem] top-1.5 size-2.5 rounded-full bg-purple-500 ring-4 ring-base-100" />
                  <div
                    role="button"
                    tabIndex={0}
                    className={cn(
                      "relative group focus-within:z-30 text-left w-full rounded-lg border border-base-300 bg-base-100 p-3 transition-all",
                      open && "cursor-pointer hover:border-blue-300 dark:hover:border-blue-800 hover:shadow-md"
                    )}
                    onClick={() => open?.()}
                    onKeyDown={(e) => e.key === "Enter" && open?.()}
                  >
                    {/* Dim the content, not the wrapper: the menu inside must stay legible. */}
                    <div className={cn(o?.hidden && "opacity-60")}>
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-purple-600 dark:text-purple-400 flex items-center gap-2 pr-8">
                        {u.label || "Undated"} {o?.hidden && <HiddenChip />}
                      </p>
                      <p className="text-sm text-base-content mt-0.5 leading-5 pr-8">{u.text}</p>
                      <Note o={o} className="mt-1.5" />
                    </div>
                    <ItemMenu onOpen={open} hidden={Boolean(o?.hidden)} hasNote={Boolean(o?.note)} onNote={() => openNote(u.key, u.text, undatedPreview(u.label, u.text))} onToggleHide={() => toggleHide(u.key)} />
                  </div>
                </Collapse>
                </li>
              );
            })}
          </ol>
        </Collapse>
        </section>
      )}

      {undatedCalls > 0 && (
        <p className="text-xs text-base-content/50 mb-4 ml-[4.5rem]">
          {undatedCalls} call{undatedCalls === 1 ? "" : "s"} without a date {undatedCalls === 1 ? "isn't" : "aren't"} shown.
        </p>
      )}

      {/* Dated chronology */}
      <div className="relative">
        {/* Continuous rail behind the day markers */}
        <div className="absolute left-[4.5rem] top-2 bottom-2 w-0.5 bg-base-300 -ml-px" />
        {days.map((day) => {
          const month = day.date.toLocaleDateString([], { month: "long", year: "numeric" });
          const showMonth = month !== lastMonth;
          lastMonth = month;
          return (
            <div key={day.key}>
            <Collapse open={day.shown > 0}>
              {showMonth && (
                <div {...enter()} className="relative flex items-center gap-3 mb-4 mt-2 first:mt-0">
                  <span className="w-[4.5rem] shrink-0" />
                  <span className="relative z-10 text-[11px] font-semibold uppercase tracking-[0.15em] text-base-content/50 bg-base-200 dark:bg-base-100 pr-2 -ml-px pl-4">{month}</span>
                </div>
              )}
              <section className="relative flex gap-5 pb-8">
                {/* Day anchor */}
                <div className="w-[4.5rem] shrink-0 sticky top-4 self-start text-right pr-5">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-base-content/50">{WEEKDAY(day.date)}</div>
                  <div className="text-3xl font-semibold leading-none tabular-nums text-base-content">{day.date.getDate()}</div>
                  <div className="text-[11px] font-medium uppercase tracking-wider text-base-content/50">{MONTH(day.date)}</div>
                  <span className="absolute -right-[5px] top-[1.15rem] size-2.5 rounded-full bg-base-content/40 ring-4 ring-base-200 dark:ring-base-100" />
                </div>

                <ol className="flex-1 min-w-0">
                  {day.items.map((it) => {
                    const o = overrides[it.key];
                    const shown = itemShown(it.key);
                    if (it.kind === "call") {
                      const c = it.call;
                      // Surface the strongest moments, then present them in call order.
                      const shownFlags = show.flag
                        ? [...it.flags]
                            .sort((a, b) => b.severity - a.severity || (a.status === "confirmed" ? -1 : 0) - (b.status === "confirmed" ? -1 : 0) || (a.start_ms || 0) - (b.start_ms || 0))
                            .slice(0, 3)
                            .sort((a, b) => (a.start_ms || 0) - (b.start_ms || 0))
                        : [];
                      const moreFlags = show.flag ? it.flags.length - shownFlags.length : 0;
                      const who = callParties(c).primary;
                      const openCall = () => navigate(`/investigations/${investigationId}/calls/${c.id}`);
                      const narrative = it.narrative;
                      const narrativeShown = narrative.some((n) => itemShown(n.key));
                      return (
                        <li key={it.key} {...enter()}>
                        <Collapse open={shown} className="pb-3">
                          <div
                            className={cn(
                              "relative focus-within:z-30 rounded-lg border border-base-300 bg-base-100 hover:border-blue-300 dark:hover:border-blue-800 hover:shadow-md transition-all cursor-pointer group",
                              newRowClass(isNewSince(c.analyzed_at, since), clearing)
                            )}
                            onClick={openCall}
                          >
                            <div className={cn("p-4", o?.hidden && "opacity-60")}>
                              <div className="flex items-start gap-3">
                                {/* App blue (secondary tile, primary-blue glyph), not the DaisyUI theme primary. */}
                                <div className="p-2 bg-blue-50 dark:bg-blue-900/40 rounded-lg shrink-0">
                                  <PhoneIcon className="h-5 w-5 text-blue-500 dark:text-blue-300" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-baseline gap-2 flex-wrap">
                                    <span className="text-xs tabular-nums font-semibold text-base-content/70">{TIME(it.at)}</span>
                                    <span className="text-sm font-medium text-base-content group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors truncate">{who}</span>
                                    {c.duration_ms ? <span className="text-xs text-base-content/40 tabular-nums">{formatMs(c.duration_ms)}</span> : null}
                                    {c.has_third_party && <span className="text-[10px] px-1.5 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-400">3-way</span>}
                                    {o?.hidden && <HiddenChip />}
                                    <span className="ml-auto flex items-center gap-1.5">
                                      {it.flags.length > 0 && (
                                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-base-content/50">
                                          <FlagIcon className={cn("size-3", it.flags.some((f) => f.severity >= 4) ? "text-red-500" : "text-amber-500")} /> {it.flags.length}
                                        </span>
                                      )}
                                      {c.relevance_score != null && (
                                        <span className={cn("text-[11px] font-bold px-2 py-0.5 rounded-full tabular-nums", scoreClass(c.relevance_score))} title="Relevance">
                                          {c.relevance_score}
                                        </span>
                                      )}
                                      <ItemMenu inline onOpen={openCall} hidden={Boolean(o?.hidden)} hasNote={Boolean(o?.note)} onNote={() => openNote(it.key, `Call · ${who} · ${TIME(it.at)}`, callPreview(c, it.at, it.narrative, it.flags.length))} onToggleHide={() => toggleHide(it.key)} />
                                    </span>
                                  </div>
                                  {narrative.length > 0 ? (
                                    narrative.map((n) => {
                                      const no = overrides[n.key];
                                      return (
                                        // Each narrative line is its own brief entry with its own menu.
                                        <Collapse key={n.key} open={itemShown(n.key)} className="relative group/line focus-within:z-30 pt-1.5 pr-6">
                                          <div className={cn(no?.hidden && "opacity-60")}>
                                            <p className="text-sm text-base-content/80 leading-5">
                                              {no?.hidden && <HiddenChip />} {n.text}
                                            </p>
                                            <Note o={no} className="mt-1" />
                                          </div>
                                          <ItemMenu
                                            compact
                                            className="!opacity-0 group-hover/line:!opacity-100"
                                            onOpen={null}
                                            hidden={Boolean(no?.hidden)}
                                            hasNote={Boolean(no?.note)}
                                            onNote={() => openNote(n.key, n.text, callPreview(c, it.at, it.narrative, it.flags.length, n.key))}
                                            onToggleHide={() => toggleHide(n.key)}
                                          />
                                        </Collapse>
                                      );
                                    })
                                  ) : c.relevance_reason || c.summary ? (
                                    <p className="text-sm text-base-content/70 leading-5 mt-1.5 line-clamp-3">{c.relevance_reason || c.summary}</p>
                                  ) : (
                                    <p className="text-xs text-base-content/40 mt-1.5 truncate">{c.file.original_file_name}</p>
                                  )}
                                  {narrative.length > 0 && !narrativeShown && <p className="text-xs text-base-content/40 mt-1.5">Narrative hidden</p>}
                                  <Note o={o} className="mt-1.5" />
                                </div>
                              </div>
                            </div>

                            {shownFlags.length > 0 && (
                              <ol className={cn("border-t border-base-200 dark:border-base-300 divide-y divide-base-200 dark:divide-base-300", o?.hidden && "opacity-60")}>
                                {shownFlags.map((f) => (
                                  <li key={f.id}>
                                    <button
                                      className="relative w-full text-left pl-5 pr-4 py-2.5 flex items-start gap-3 hover:bg-base-200/60 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        play(investigationId, c.id, f.start_ms || 0);
                                      }}
                                    >
                                      <span className={cn("absolute left-0 top-0 bottom-0 w-1", f.severity >= 4 ? "bg-red-500" : "bg-amber-500")} />
                                      <span className="text-[11px] tabular-nums text-base-content/50 w-9 shrink-0 pt-0.5">{formatMs(f.start_ms)}</span>
                                      <span className="min-w-0 flex-1">
                                        <span className="flex items-center gap-1.5 flex-wrap">
                                          <span className={cn("text-[10px] font-semibold px-1.5 rounded", SEVERITY_CLASSES[f.severity])}>sev {f.severity}</span>
                                          <span className="text-xs font-semibold text-base-content">{LEAD_CATEGORY_LABELS[f.category] || f.category}</span>
                                          {f.status === "confirmed" && (
                                            <span className="inline-flex items-center gap-0.5 text-[10px] text-green-700 dark:text-green-400"><CheckIcon className="size-3" /> confirmed</span>
                                          )}
                                        </span>
                                        <span className="block text-sm italic text-base-content/80 leading-5 mt-0.5 line-clamp-2">“{f.quote_translated || f.quote}”</span>
                                      </span>
                                    </button>
                                  </li>
                                ))}
                                {moreFlags > 0 && (
                                  <li className="px-5 py-2 text-xs text-base-content/50">+{moreFlags} more flagged moment{moreFlags === 1 ? "" : "s"} in this call</li>
                                )}
                              </ol>
                            )}
                          </div>
                        </Collapse>
                        </li>
                      );
                    }
                    if (it.kind === "file") {
                      const f = it.file;
                      const desc = (f.metadata as any)?.description as string | undefined;
                      const openFile = () => navigate(`/investigations/${investigationId}/files/${f.id}`);
                      return (
                        <li key={it.key} {...enter()}>
                        <Collapse open={shown} className="pb-3">
                          <div
                            className={cn(
                              "relative focus-within:z-30 rounded-lg border border-base-300 bg-base-100 p-3 flex items-start gap-3 hover:border-blue-300 dark:hover:border-blue-800 hover:shadow-md transition-all cursor-pointer group",
                              newRowClass(isNewSince(f.created_at, since), clearing)
                            )}
                            onClick={openFile}
                          >
                            <ItemMenu onOpen={openFile} hidden={Boolean(o?.hidden)} hasNote={Boolean(o?.note)} onNote={() =>
                              openNote(
                                it.key,
                                f.original_file_name,
                                filePreview(
                                  f,
                                  it.anchored ? (
                                    <span className="text-[11px] font-semibold uppercase tracking-wider text-base-content/40">
                                      {FILE_KIND_LABEL[f.kind] || f.kind} · {f.kind === "spreadsheet" ? "records" : "dated"} {SPAN_DATE(it.at)}
                                      {it.covers ? ` – ${SPAN_DATE(it.covers)}` : ""}
                                    </span>
                                  ) : (
                                    <>
                                      <span className="text-xs tabular-nums font-semibold text-base-content/70">{TIME(it.at)}</span>
                                      <span className="text-[11px] font-semibold uppercase tracking-wider text-base-content/40">{FILE_KIND_LABEL[f.kind] || f.kind} added</span>
                                    </>
                                  )
                                )
                              )
                            } onToggleHide={() => toggleHide(it.key)} />
                            <div className={cn("p-2 bg-base-200 rounded-lg shrink-0", o?.hidden && "opacity-60")}>
                              <DocumentTextIcon className="h-5 w-5 text-base-content/50" />
                            </div>
                            <div className={cn("min-w-0 flex-1 pr-6", o?.hidden && "opacity-60")}>
                              <div className="flex items-baseline gap-2 flex-wrap">
                                {it.anchored ? (
                                  <span className="text-[11px] font-semibold uppercase tracking-wider text-base-content/40">
                                    {FILE_KIND_LABEL[f.kind] || f.kind}
                                    {" · "}
                                    {f.kind === "spreadsheet" ? "records" : "dated"} {SPAN_DATE(it.at)}
                                    {it.covers ? ` – ${SPAN_DATE(it.covers)}` : ""}
                                  </span>
                                ) : (
                                  <>
                                    <span className="text-xs tabular-nums font-semibold text-base-content/70">{TIME(it.at)}</span>
                                    <span className="text-[11px] font-semibold uppercase tracking-wider text-base-content/40">{FILE_KIND_LABEL[f.kind] || f.kind} added</span>
                                  </>
                                )}
                                {o?.hidden && <HiddenChip />}
                              </div>
                              <p className="text-sm font-medium text-base-content truncate group-hover:text-blue-600 dark:group-hover:text-blue-300 transition-colors">{f.original_file_name}</p>
                              {desc && <p className="text-xs text-base-content/60 line-clamp-2 mt-0.5">{desc}</p>}
                              {it.anchored && <p className="text-[11px] text-base-content/40 mt-1">Added to the investigation {it.addedAt.toLocaleDateString()}</p>}
                              <Note o={o} className="mt-1.5" />
                            </div>
                          </div>
                        </Collapse>
                        </li>
                      );
                    }
                    // Most precise source first: the flagged lead (exact
                    // second or row), then the document page, then the call.
                    // A source deleted since the brief was written resolves
                    // to nothing and the card quietly stays inert.
                    const open = (() => {
                      if (it.leadId) {
                        const l = leads.find((x) => x.id === it.leadId);
                        if (l?.video_anchor) return () => navigate(`/investigations/${investigationId}/files/${l.file_id}?lead=${l.id}`);
                        if (l?.call_id) return () => navigate(`/investigations/${investigationId}/calls/${l.call_id}?t=${l.start_ms || 0}`);
                        if (l) return () => navigate(`/investigations/${investigationId}/files/${l.file_id}?lead=${l.id}`);
                      }
                      if (it.fileId && files.some((f) => f.id === it.fileId))
                        return () => navigate(`/investigations/${investigationId}/files/${it.fileId}${it.page ? `?page=${it.page}` : ""}`);
                      if (it.callId) return () => navigate(`/investigations/${investigationId}/calls/${it.callId}`);
                      return null;
                    })();
                    return (
                      <li key={it.key} {...enter()}>
                      <Collapse open={shown} className="pb-3">
                        <div
                          role="button"
                          tabIndex={0}
                          className={cn(
                            "relative group focus-within:z-30 w-full text-left rounded-lg border border-purple-200 dark:border-purple-900 bg-purple-50/60 dark:bg-purple-950/20 p-3 flex items-start gap-3 transition-all",
                            open && "cursor-pointer hover:border-purple-300 hover:shadow-md"
                          )}
                          title={open ? "Open the source" : undefined}
                          onClick={() => open?.()}
                          onKeyDown={(e) => e.key === "Enter" && open?.()}
                        >
                          <SparklesIcon className={cn("size-4 text-purple-500 shrink-0 mt-0.5", o?.hidden && "opacity-60")} />
                          <div className={cn("min-w-0 flex-1 pr-6", o?.hidden && "opacity-60")}>
                            <p className="text-sm text-base-content/80 leading-5">
                              {o?.hidden && <HiddenChip />} {it.text}
                            </p>
                            <Note o={o} className="mt-1.5" />
                          </div>
                          <ItemMenu onOpen={open} hidden={Boolean(o?.hidden)} hasNote={Boolean(o?.note)} onNote={() => openNote(it.key, it.text, briefPreview(it.text))} onToggleHide={() => toggleHide(it.key)} />
                        </div>
                      </Collapse>
                      </li>
                    );
                  })}
                </ol>
              </section>
            </Collapse>
            </div>
          );
        })}
      </div>

      <TimelineNoteModal target={noteTarget} onClose={() => setNoteTarget(null)} onSave={(note) => setOverride(noteTarget!.key, { note })} />
    </div>
  );
};

export default TimelineTab;
